/**
 * Wallet Modal
 *
 * Custom wallet selection modal using Tailwind CSS.
 * Uses ConnectorKit's useConnector hook.
 */

import { useConnector } from "@solana/connector/react";
import type { WalletConnectorMetadata } from "@solana/connector/react";
import {
    ArrowShortUpRightIcon,
    CrossIcon
} from "@solomon-labs/design/components/icons";
import {
    Alert,
    AlertDescription,
    AlertTitle,
    Badge,
    Button,
    ButtonIcon,
    Dialog,
    DialogClose,
    DialogContent,
    DialogTitle,
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerTitle,
    IndicationUnit
} from "@solomon-labs/design/components/ui";
import posthog from "posthog-js";
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import { analyticsContext, hashForAnalytics } from "../analytics/posthog";
import {
    captureWalletEvent,
    type WalletSource
} from "../analytics/walletEvents";
import { walletJourney } from "../analytics/walletJourney";
// Wallet icons
import phantomIcon from "../assets/wallet-icons/phantom.svg";
import solflareIcon from "../assets/wallet-icons/solflare.svg";
import { ListItem } from "../components/Data/List";
import { Text } from "../components/ui/text";
import { useGeoRestriction } from "../hooks/geo/useGeoRestriction";
import { useIsMobileViewport } from "../hooks/useIsMobileViewport";
import { fetchWalletStats } from "../utils/walletStats";

import {
    getWalletConnectionError,
    WALLET_RESET_ERROR,
    type WalletConnectionError
} from "./connectionFeedback";
import { useDelayedWalletHint } from "./hooks/useDelayedWalletHint";
import { useLastUsedWallet } from "./hooks/useLastUsedWallet";
import { useWallet } from "./hooks/useWallet";
import {
    isMobileBrowser,
    isMobileWalletAdapterConnector,
    isMobileWalletAdapterSupportedEnvironment
} from "./mobileWalletAdapter";
import { WalletConnectingModal } from "./WalletConnectingModal";
import type { ConnectingStep } from "./WalletConnectingModal";
import { WalletGeoRestrictionModal } from "./WalletGeoRestrictionModal";

// ============================================================================
// Popular Wallets Configuration
// ============================================================================

type AccessCheckResult = boolean | "error";

interface PopularWallet {
    id: string;
    name: string;
    icon: string;
    installUrl: string;
    /** Optional mobile deep-link builder to open app browser with current dapp URL */
    mobileBrowseDeepLink?: (dappUrl: string, referrerUrl: string) => string;
    /** Matching patterns for connector IDs/names (case-insensitive) */
    matchPatterns: string[];
}

interface WalletItem {
    id: string;
    name: string;
    icon: string;
    installed: boolean;
    connector?: WalletConnectorMetadata;
    installUrl?: string;
}

interface ConnectingWalletInfo {
    id: string;
    name: string;
    icon: string;
}

/**
 * List of popular Solana wallets to display even if not installed.
 * Users can click to install if the wallet isn't detected.
 */
const FEATURED_WALLETS: PopularWallet[] = [
    {
        id: "phantom",
        name: "Phantom",
        icon: phantomIcon,
        installUrl: "https://phantom.app/download",
        mobileBrowseDeepLink: (dappUrl, referrerUrl) =>
            `https://phantom.app/ul/browse/${encodeURIComponent(dappUrl)}?ref=${encodeURIComponent(referrerUrl)}`,
        matchPatterns: ["phantom"]
    },
    {
        id: "solflare",
        name: "Solflare",
        icon: solflareIcon,
        installUrl: "https://solflare.com/download",
        mobileBrowseDeepLink: (dappUrl, referrerUrl) =>
            `https://solflare.com/ul/v1/browse/${encodeURIComponent(dappUrl)}?ref=${encodeURIComponent(referrerUrl)}`,
        matchPatterns: ["solflare"]
    },
    {
        id: "backpack",
        name: "Backpack",
        icon: "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTI4IiBoZWlnaHQ9IjEyOCIgdmlld0JveD0iMCAwIDEyOCAxMjgiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMjgiIGhlaWdodD0iMTI4IiByeD0iMjYiIGZpbGw9IiNFMzM0MzAiLz4KPHBhdGggZD0iTTY0IDI4QzQ4LjUzNiAyOCAzNiA0MC41MzYgMzYgNTZWNzJDMzYgODcuNDY0IDQ4LjUzNiAxMDAgNjQgMTAwQzc5LjQ2NCAxMDAgOTIgODcuNDY0IDkyIDcyVjU2QzkyIDQwLjUzNiA3OS40NjQgMjggNjQgMjhaIiBmaWxsPSJ3aGl0ZSIvPgo8cGF0aCBkPSJNNTIgNjBDNTIgNTcuNzkwOSA1My43OTA5IDU2IDU2IDU2SDcyQzc0LjIwOTEgNTYgNzYgNTcuNzkwOSA3NiA2MFY3NkM3NiA3OC4yMDkxIDc0LjIwOTEgODAgNzIgODBINTZDNTMuNzkwOSA4MCA1MiA3OC4yMDkxIDUyIDc2VjYwWiIgZmlsbD0iI0UzMzQzMCIvPgo8L3N2Zz4=",
        installUrl: "https://backpack.app/download",
        mobileBrowseDeepLink: (dappUrl, referrerUrl) =>
            `https://backpack.app/ul/v1/browse/${encodeURIComponent(dappUrl)}?ref=${encodeURIComponent(referrerUrl)}`,
        matchPatterns: ["backpack"]
    }
];

const AUTO_RECONNECT_TIMEOUT_MS = 5_000;
const CONNECTED_AUTO_CLOSE_DELAY_MS = 250;
const CONNECTED_MANUAL_FALLBACK_DELAY_MS = 3_000;

function resolveWalletExternalUrl(
    wallet: WalletItem,
    isMobile: boolean
): { url: string; isMobileBrowseLink: boolean } {
    const fallbackUrl = wallet.installUrl ?? "#";
    if (!isMobile || typeof window === "undefined") {
        return { url: fallbackUrl, isMobileBrowseLink: false };
    }

    const popularWallet = FEATURED_WALLETS.find((pw) => pw.id === wallet.id);
    if (!popularWallet?.mobileBrowseDeepLink) {
        return { url: fallbackUrl, isMobileBrowseLink: false };
    }

    return {
        url: popularWallet.mobileBrowseDeepLink(
            window.location.href,
            window.location.origin
        ),
        isMobileBrowseLink: true
    };
}

// ============================================================================
// Context
// ============================================================================

interface WalletModalContextValue {
    /** Whether the wallet modal is currently open */
    showModal: boolean;
    /** True briefly after a wallet connection attempt fails (rejection,
     *  timeout, etc.) so UI can surface an error indicator. Auto-clears. */
    connectError: boolean;
    /** Current step of the connecting-flow state machine (null when idle).
     *  Exposed so other UI — e.g. the NavigationBar wallet button — can
     *  align with the same step timings (min-dwells, etc.) as the modal. */
    connectingStep: ConnectingStep | null;
    /** Open or close the wallet modal */
    setShowModal: (show: boolean) => void;
    /** Open the wallet modal */
    openModal: (source?: WalletSource) => void;
    /** Close the wallet modal */
    closeModal: (reason?: string) => void;
    /** Whether the connecting flow modal is active (prevents ToS from opening) */
    isConnectingFlow: boolean;
    /** True once the wallet has cleared any access/whitelist check — apps
     *  without an access check are allowed immediately, apps with a check
     *  flip this true only after `reportAccessCheck(true)`. Consumers should
     *  treat `connected && !isAccessAllowed` as still-pending. */
    isAccessAllowed: boolean;
    /** Register an access check (call from apps that have whitelist logic) */
    registerAccessCheck: () => void;
    /** Report the result of the access check */
    reportAccessCheck: (result: AccessCheckResult) => void;
    /** Force the next connection to simulate a denied access check.
     *  Showcase-only — resets after one use. */
    simulateDenied: () => void;
}

/** How long the connectError flag stays true after a failed connect attempt. */
const CONNECT_ERROR_DWELL_MS = 3000;

const WalletModalContext = createContext<WalletModalContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface WalletModalProviderProps {
    children: React.ReactNode;
}

/**
 * Provider for wallet modal state management
 *
 * Renders the wallet selection modal and provides context for programmatic control.
 */
export function WalletModalProvider({
    children
}: WalletModalProviderProps): React.ReactElement {
    const visibleRef = useRef(false);
    const connectStartedAtRef = useRef<number | null>(null);
    const connectedFiredRef = useRef(false);
    const [showModal, setShowModalState] = useState(false);
    const [connectError, setConnectError] = useState(false);
    const connectErrorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
        null
    );
    const [isMobile] = useState(() => isMobileBrowser());
    const isMobileViewport = useIsMobileViewport();
    const [isMobileWalletAdapterSupported] = useState(() =>
        isMobileWalletAdapterSupportedEnvironment()
    );
    const [connectingId, setConnectingId] = useState<string | null>(null);
    const [connectingStep, setConnectingStep] = useState<ConnectingStep | null>(
        null
    );
    const [connectingWallet, setConnectingWallet] =
        useState<ConnectingWalletInfo | null>(null);
    const [showGoToSiteButton, setShowGoToSiteButton] = useState(false);
    const [connectionFailure, setConnectionFailure] =
        useState<WalletConnectionError | null>(null);
    const [isResettingConnection, setIsResettingConnection] = useState(false);
    const resetInProgressRef = useRef(false);
    const blockedResetWalletRef = useRef<ConnectingWalletInfo | null>(null);
    const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const activeAttemptRef = useRef<{
        id: string;
        wallet: ConnectingWalletInfo;
    } | null>(null);

    // Track card height across modal steps so each step matches the
    // previous one's height. Connect Wallet → Connecting → Checking → Connected.
    const [lastCardHeight, setLastCardHeight] = useState<number | null>(null);
    const measureCardRef = useCallback((node: HTMLDivElement | null) => {
        if (node) {
            setLastCardHeight(node.offsetHeight);
        }
    }, []);

    // Use the combined useConnector hook
    const {
        connectors,
        connector,
        connectWallet,
        disconnectWallet,
        isConnected,
        isConnecting
    } = useConnector();

    const lastUsedWalletId = useLastUsedWallet(
        isConnected && connector ? connector.id : null
    );

    // Use useWallet for isWalletReady (stabilization delay) and publicKey
    const { isWalletReady, publicKey } = useWallet();

    // Geo restriction check — blocks wallet connection for restricted locations.
    // Bypassed on /showcase/* routes so developers can test wallet flows.
    const {
        isRestricted: rawGeoRestricted,
        ip: geoIp,
        country: geoCountry,
        failed: geoFailed
    } = useGeoRestriction();
    const isShowcase =
        typeof window !== "undefined" &&
        window.location.pathname.startsWith("/showcase/");
    const isGeoRestricted = isShowcase ? false : rawGeoRestricted;

    // Access check injection from main app. We keep both a ref and a state:
    //   - ref  → read synchronously in the checking→connected transition
    //            effect so it cannot short-circuit due to render timing
    //   - state → so the derived `isAccessAllowed` is reactive in context
    //            consumers (e.g. the NavigationBar wallet button)
    const accessCheckRegisteredRef = useRef(false);
    const [accessCheckRegistered, setAccessCheckRegistered] = useState(false);
    const [accessCheckResult, setAccessCheckResult] =
        useState<AccessCheckResult | null>(null);

    // Create a unified wallet list: installed wallets + uninstalled popular wallets
    const walletList = useMemo(() => {
        const items: WalletItem[] = [];
        const matchedPopularIds = new Set<string>();

        // First, add all detected/installed connectors
        for (const connector of connectors) {
            if (
                isMobileWalletAdapterConnector(connector) &&
                !isMobileWalletAdapterSupported
            ) {
                continue;
            }

            const connectorIdLower = connector.id.toLowerCase();
            const connectorNameLower = connector.name.toLowerCase();

            // Check if this connector matches any popular wallet
            const matchedPopular = FEATURED_WALLETS.find((pw) =>
                pw.matchPatterns.some(
                    (pattern) =>
                        connectorIdLower.includes(pattern.toLowerCase()) ||
                        connectorNameLower.includes(pattern.toLowerCase())
                )
            );

            if (matchedPopular) {
                matchedPopularIds.add(matchedPopular.id);
            }

            items.push({
                id: connector.id,
                name: connector.name,
                icon: connector.icon || "",
                installed: true,
                connector
            });
        }

        // Then, add popular wallets that weren't detected (uninstalled)
        for (const popularWallet of FEATURED_WALLETS) {
            if (!matchedPopularIds.has(popularWallet.id)) {
                items.push({
                    id: popularWallet.id,
                    name: popularWallet.name,
                    icon: popularWallet.icon,
                    installed: false,
                    installUrl: popularWallet.installUrl
                });
            }
        }

        return items.sort(
            (a, b) =>
                Number(b.installed && b.id === lastUsedWalletId) -
                Number(a.installed && a.id === lastUsedWalletId)
        );
    }, [connectors, isMobileWalletAdapterSupported, lastUsedWalletId]);

    const hasBrowserWallet = connectors.some(
        (wallet) =>
            !isMobileWalletAdapterConnector(wallet) &&
            wallet.id !== "walletconnect"
    );
    const showDetectionWarning = useDelayedWalletHint(
        showModal &&
            !isGeoRestricted &&
            connectingStep === null &&
            !isMobile &&
            !hasBrowserWallet,
        3_000
    );

    // Minimum display time refs for "connecting" and "checking" steps
    const connectingMinElapsedRef = useRef(false);
    const connectingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
        null
    );
    const checkingMinElapsedRef = useRef(false);
    const checkingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Wall-clock timestamp when the user picked a provider — used to compute
    const closeModal = useCallback(
        (reason = "unknown"): void => {
            if (
                visibleRef.current &&
                analyticsContext().app !== "main" &&
                connectingStep === null
            )
                posthog.capture("wallet_selector_dismissed");
            visibleRef.current = false;
            walletJourney.close(reason);
            setShowModalState(false);
            setConnectingId(null);
            setConnectionFailure(null);
            setConnectingStep(null);
            setConnectingWallet(null);
            setShowGoToSiteButton(false);
            connectingMinElapsedRef.current = false;
            checkingMinElapsedRef.current = false;
        },
        [connectingStep]
    );

    useEffect(() => {
        if (!showModal) return;
        const state = isGeoRestricted
            ? geoFailed
                ? "location_error"
                : "location_restricted"
            : connectingStep === "not-whitelisted"
              ? "access_denied"
              : connectingStep === "access-error"
                ? "access_error"
                : (connectingStep ?? "selector");
        walletJourney.viewState(
            state,
            state === "selector"
                ? {
                      available_providers: walletList
                          .filter((wallet) => wallet.connector?.ready)
                          .map((wallet) => wallet.name),
                      installed_providers: walletList
                          .filter((wallet) => wallet.installed)
                          .map((wallet) => wallet.name)
                  }
                : {}
        );
    }, [showModal, isGeoRestricted, geoFailed, connectingStep, walletList]);

    // Reset the access-check result when the wallet disconnects so the next
    // connect starts fresh. Keeping it across modal close (while still
    // connected) is what lets `isAccessAllowed` stay true after auto-close.
    useEffect(() => {
        if (!isConnected) {
            setAccessCheckResult(null);
        }
    }, [isConnected]);

    const openModal = useCallback(
        (source: WalletSource = "unknown"): void => {
            // Clear any stale error from a previous attempt.
            if (connectErrorTimerRef.current !== null) {
                clearTimeout(connectErrorTimerRef.current);
                connectErrorTimerRef.current = null;
            }
            setConnectError(false);
            if (!visibleRef.current && analyticsContext().app !== "main")
                posthog.capture("wallet_selector_opened", {
                    surface: isMobile ? "mobile_drawer" : "modal"
                });
            visibleRef.current = true;
            walletJourney.open(
                source,
                isMobileViewport ? "drawer" : "dialog",
                source !== "unknown"
            );
            const pending = activeAttemptRef.current;
            if (pending) {
                setConnectingId(pending.wallet.id);
                setConnectingWallet(pending.wallet);
                setConnectingStep("connecting");
            }
            if (blockedResetWalletRef.current) {
                setConnectingWallet(blockedResetWalletRef.current);
                setConnectionFailure(WALLET_RESET_ERROR);
                setConnectingStep("connection-error");
            }
            setShowModalState(true);
        },
        [isMobile, isMobileViewport]
    );

    const setShowModal = useCallback(
        (show: boolean): void => {
            if (show) {
                openModal();
                return;
            }
            closeModal();
        },
        [openModal, closeModal]
    );

    const simulateDeniedRef = useRef(false);

    const registerAccessCheck = useCallback((): void => {
        accessCheckRegisteredRef.current = true;
        setAccessCheckRegistered(true);
    }, []);

    const reportAccessCheck = useCallback((result: AccessCheckResult): void => {
        if (simulateDeniedRef.current) {
            // Force denied result, then clear the flag
            simulateDeniedRef.current = false;
            setAccessCheckResult(false);
            return;
        }
        setAccessCheckResult(result);
    }, []);

    const simulateDenied = useCallback((): void => {
        simulateDeniedRef.current = true;
    }, []);

    // Minimum display time for "connecting" step
    const [connectingTick, setConnectingTick] = useState(0);

    useEffect(() => {
        if (connectingStep === "connecting") {
            connectingMinElapsedRef.current = false;
            connectingTimerRef.current = setTimeout(() => {
                connectingMinElapsedRef.current = true;
                setConnectingTick((t) => t + 1);
            }, 1500);
        }
        return (): void => {
            if (connectingTimerRef.current) {
                clearTimeout(connectingTimerRef.current);
                connectingTimerRef.current = null;
            }
        };
    }, [connectingStep]);

    // Auto-reconnection timeout: if ConnectorKit is auto-reconnecting on page refresh
    // and the service worker is dead, isConnecting stays true forever.
    // Reset after a short timeout since no user interaction is needed.
    useEffect(() => {
        if (
            isConnecting &&
            connectingStep === null &&
            !activeAttemptRef.current &&
            !resetInProgressRef.current
        ) {
            const timer = setTimeout(() => {
                walletJourney.restoreTimeout();
                void disconnectWallet();
            }, AUTO_RECONNECT_TIMEOUT_MS);
            return (): void => {
                clearTimeout(timer);
            };
        }
    }, [isConnecting, connectingStep, disconnectWallet]);

    // Step transition: connecting -> checking (when wallet extension approves + min time elapsed)
    useEffect(() => {
        if (connectingStep !== "connecting" || !isConnected) return;
        if (!connectingMinElapsedRef.current) return;
        setConnectingStep("checking");
    }, [isConnected, connectingStep, connectingTick]);

    // Step transition: checking -> connected or not-whitelisted
    // Enforce a minimum display time so the "Checking" step is visible
    useEffect(() => {
        if (connectingStep === "checking") {
            checkingMinElapsedRef.current = false;
            checkingTimerRef.current = setTimeout(() => {
                checkingMinElapsedRef.current = true;
                // Re-trigger the transition effect by updating a state tick
                setCheckingTick((t) => t + 1);
            }, 1500);
        }
        return (): void => {
            if (checkingTimerRef.current) {
                clearTimeout(checkingTimerRef.current);
                checkingTimerRef.current = null;
            }
        };
    }, [connectingStep]);

    const [checkingTick, setCheckingTick] = useState(0);

    useEffect(() => {
        if (connectingStep !== "checking" || !isWalletReady) return;
        if (!checkingMinElapsedRef.current) return; // wait for minimum display time

        if (!accessCheckRegisteredRef.current) {
            // No access check registered (e.g. metrics app) — go straight to connected
            setConnectingStep("connected");
            return;
        }

        if (accessCheckResult === null) return; // still waiting for result
        if (accessCheckResult === "error") {
            setConnectingStep("access-error");
            return;
        }
        setConnectingStep(accessCheckResult ? "connected" : "not-whitelisted");
    }, [isWalletReady, connectingStep, accessCheckResult, checkingTick]);

    // Fire `wallet_connected` + `posthog.identify(...)` once when the modal
    // step transitions to "connected". `connectedFiredRef` guards re-runs.
    useEffect(() => {
        if (analyticsContext().app === "main" || connectingStep !== "connected")
            return;
        if (connectedFiredRef.current) return;
        if (!publicKey || !connectingWallet) return;

        connectedFiredRef.current = true;
        const startedAt = connectStartedAtRef.current;
        const latency = startedAt !== null ? Date.now() - startedAt : undefined;
        connectStartedAtRef.current = null;

        const walletProvider = connectingWallet.name;
        const publicKeyStr = publicKey.toString();

        let isFirstConnect = false;
        try {
            isFirstConnect =
                window.localStorage.getItem("solomon:has_connected") === null;
            window.localStorage.setItem("solomon:has_connected", "1");
        } catch {
            // localStorage unavailable (e.g. in-app browsers, privacy modes) —
            // leave isFirstConnect=false; the property simply won't reliably
            // distinguish first-vs-returning for those sessions.
        }

        void hashForAnalytics(publicKeyStr).then((walletId) => {
            if (walletId) {
                posthog.identify(walletId, {
                    wallet_provider: walletProvider
                });
            }
            posthog.capture("wallet_connected", {
                wallet_provider: walletProvider,
                connect_latency_ms: latency,
                is_first_connect: isFirstConnect
            });
        });

        // Sybil-filter person properties — fired separately from the synchronous
        // wallet_connected event so the connect flow is never blocked on RPC.
        // Failures and timeouts collapse to zero stats inside fetchWalletStats.
        void fetchWalletStats(publicKey).then((stats) => {
            posthog.setPersonProperties(stats);
        });
    }, [connectingStep, publicKey, connectingWallet]);

    // Auto-close almost immediately once the wallet is fully connected. The
    // short settle beat lets the connected state render without holding users
    // on a fake success screen.
    const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
        null
    );
    useEffect(() => {
        if (connectingStep === "connected") {
            autoCloseTimerRef.current = setTimeout(() => {
                closeModal("completed");
            }, CONNECTED_AUTO_CLOSE_DELAY_MS);
        }
        return (): void => {
            if (autoCloseTimerRef.current) {
                clearTimeout(autoCloseTimerRef.current);
                autoCloseTimerRef.current = null;
            }
        };
    }, [connectingStep, closeModal]);

    // Show "Go to Site Manually" after the fallback delay on "connected".
    const goToSiteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        if (connectingStep === "connected") {
            goToSiteTimerRef.current = setTimeout(() => {
                setShowGoToSiteButton(true);
            }, CONNECTED_MANUAL_FALLBACK_DELAY_MS);
        }
        return (): void => {
            if (goToSiteTimerRef.current) {
                clearTimeout(goToSiteTimerRef.current);
                goToSiteTimerRef.current = null;
            }
        };
    }, [connectingStep]);

    const handleWalletSelect = async (wallet: WalletItem): Promise<void> => {
        if (isConnected) {
            closeModal();
            return;
        }

        if (
            activeAttemptRef.current ||
            resetInProgressRef.current ||
            blockedResetWalletRef.current ||
            connectingId ||
            isConnecting ||
            !wallet.connector?.ready
        ) {
            return;
        }

        connectStartedAtRef.current = Date.now();
        connectedFiredRef.current = false;
        if (analyticsContext().app !== "main")
            posthog.capture("wallet_provider_selected", {
                wallet_provider: wallet.name
            });
        const attemptId = walletJourney.start(
            wallet.name,
            isMobileWalletAdapterConnector(wallet.connector)
                ? "mobile_wallet_adapter"
                : "wallet_standard"
        );

        activeAttemptRef.current = {
            id: attemptId,
            wallet: { id: wallet.id, name: wallet.name, icon: wallet.icon }
        };
        setConnectionFailure(null);
        setConnectError(false);
        // Capture wallet info and enter connecting flow
        setConnectingId(wallet.connector.id);
        setConnectingWallet({
            id: wallet.id,
            name: wallet.name,
            icon: wallet.icon
        });
        setConnectingStep("connecting");

        try {
            await connectWallet(wallet.connector.id);
        } catch (error) {
            if (activeAttemptRef.current?.id !== attemptId) return;
            walletJourney.failed(attemptId, error);
            if (analyticsContext().app !== "main")
                posthog.capture("wallet_connect_failed", {
                    wallet_provider: wallet.name,
                    error_code: "unknown"
                });

            // Keep recovery in the modal. A late failure must not reopen it.
            if (visibleRef.current) {
                setConnectionFailure(getWalletConnectionError(error));
                setConnectingStep("connection-error");
            }
            setConnectingId(null);
            setConnectError(true);
            if (connectErrorTimerRef.current !== null) {
                clearTimeout(connectErrorTimerRef.current);
            }
            connectErrorTimerRef.current = setTimeout(() => {
                setConnectError(false);
                connectErrorTimerRef.current = null;
            }, CONNECT_ERROR_DWELL_MS);
        } finally {
            if (activeAttemptRef.current?.id === attemptId) {
                activeAttemptRef.current = null;
                setConnectingId(null);
            }
        }
    };

    const chooseAnotherWallet = async (): Promise<void> => {
        if (resetInProgressRef.current || blockedResetWalletRef.current) return;
        resetInProgressRef.current = true;
        setIsResettingConnection(true);
        const pending = activeAttemptRef.current;
        activeAttemptRef.current = null;
        if (pending)
            walletJourney.failed(pending.id, new Error("Connection cancelled"));
        try {
            // ConnectorKit invalidates the old request and clears restore storage.
            // Wait for cleanup before allowing another provider request.
            await Promise.race([
                disconnectWallet(),
                new Promise<never>((_, reject) => {
                    resetTimerRef.current = setTimeout(
                        () => reject(new Error("Reset timeout")),
                        5_000
                    );
                })
            ]);
            setConnectingStep(null);
            setConnectingWallet(null);
            setConnectingId(null);
            setConnectionFailure(null);
        } catch {
            // Do not start a competing request after an incomplete reset.
            blockedResetWalletRef.current = connectingWallet;
            if (visibleRef.current) {
                setConnectingWallet(connectingWallet);
                setConnectionFailure(WALLET_RESET_ERROR);
                setConnectingStep("connection-error");
            }
        } finally {
            if (resetTimerRef.current !== null) {
                clearTimeout(resetTimerRef.current);
                resetTimerRef.current = null;
            }
            resetInProgressRef.current = false;
            setIsResettingConnection(false);
        }
    };

    const retryConnection = (): void => {
        if (blockedResetWalletRef.current) {
            window.location.reload();
            return;
        }
        if (!connectingWallet || resetInProgressRef.current) return;
        const wallet = walletList.find(
            (item) => item.id === connectingWallet.id
        );
        if (!wallet?.connector?.ready) {
            setConnectionFailure(
                getWalletConnectionError(new Error("Wallet unavailable"))
            );
            return;
        }
        void handleWalletSelect(wallet);
    };

    useEffect(
        () => (): void => {
            if (resetTimerRef.current !== null)
                clearTimeout(resetTimerRef.current);
            if (connectErrorTimerRef.current !== null)
                clearTimeout(connectErrorTimerRef.current);
        },
        []
    );

    const isConnectingFlow = connectingStep !== null;

    // Allowed when the app opted out of the access check, or when the check
    // has returned positive. `connected && !isAccessAllowed` means the wallet
    // is physically connected but we're still (or failed) verifying access.
    const isAccessAllowed =
        !accessCheckRegistered || accessCheckResult === true;

    const value: WalletModalContextValue = {
        showModal,
        connectError,
        connectingStep,
        setShowModal,
        openModal,
        closeModal,
        isConnectingFlow,
        isAccessAllowed,
        registerAccessCheck,
        reportAccessCheck,
        simulateDenied
    };

    return (
        <WalletModalContext.Provider value={value}>
            {children}

            {/* Geo Restriction Modal */}
            {showModal && isGeoRestricted && (
                <WalletGeoRestrictionModal
                    ip={geoIp}
                    country={geoCountry}
                    onClose={closeModal}
                />
            )}

            {/* Connecting Flow Modal */}
            {showModal &&
                !isGeoRestricted &&
                connectingStep !== null &&
                connectingWallet !== null && (
                    <WalletConnectingModal
                        step={connectingStep}
                        connectionError={connectionFailure}
                        onRetry={retryConnection}
                        onChooseWallet={(): void => {
                            void chooseAnotherWallet();
                        }}
                        isResettingConnection={isResettingConnection}
                        walletName={connectingWallet.name}
                        walletIcon={connectingWallet.icon}
                        walletAddress={publicKey?.toString()}
                        onClose={closeModal}
                        onGoToSite={closeModal}
                        showGoToSite={showGoToSiteButton}
                        minHeight={lastCardHeight}
                        onMeasure={measureCardRef}
                    />
                )}

            {/* Wallet Selection — Drawer on mobile, Dialog on desktop */}
            {((): React.ReactElement => {
                const open =
                    showModal && !isGeoRestricted && connectingStep === null;
                const handleOpenChange = (v: boolean): void => {
                    if (!v) closeModal();
                };

                const header = (
                    <div className="flex items-center justify-between px-4 pb-3">
                        <div className="flex items-start gap-1">
                            <div className="pt-[7px]">
                                <div className="w-1 h-1 bg-accent" />
                            </div>
                            {isMobileViewport ? (
                                <DrawerTitle>Connect Wallet</DrawerTitle>
                            ) : (
                                <DialogTitle>Connect Wallet</DialogTitle>
                            )}
                        </div>
                        {isMobileViewport ? (
                            <DrawerClose
                                onClick={(): void => closeModal("close_button")}
                                render={
                                    <ButtonIcon
                                        variant="transparent"
                                        size="xl"
                                        aria-label="Close"
                                        icon={<CrossIcon />}
                                    />
                                }
                            />
                        ) : (
                            <DialogClose
                                onClick={(): void => closeModal("close_button")}
                                render={
                                    <ButtonIcon
                                        variant="transparent"
                                        size="xl"
                                        aria-label="Close"
                                        icon={<CrossIcon />}
                                    />
                                }
                            />
                        )}
                    </div>
                );

                const body = (
                    <>
                        <div className="flex flex-col px-4">
                            {walletList.map((wallet) => {
                                const isAnyConnecting =
                                    connectingId !== null || isConnecting;

                                if (wallet.installed && wallet.connector) {
                                    const isConnectorReady =
                                        wallet.connector.ready;
                                    return (
                                        <Button
                                            key={wallet.id}
                                            variant="transparent"
                                            interaction="default"
                                            fullWidth
                                            disabled={
                                                !isConnectorReady ||
                                                isAnyConnecting ||
                                                isResettingConnection
                                            }
                                            className="h-auto min-h-12 justify-start border-b border-line-base px-2 py-3 text-left [&_[data-slot=button-face]]:w-full [&_[data-slot=button-label]]:flex-1 [&_[data-slot=button-trailing]]:shrink-0"
                                            leading={
                                                wallet.icon ? (
                                                    <img
                                                        src={wallet.icon}
                                                        alt={wallet.name}
                                                        className="size-6"
                                                    />
                                                ) : undefined
                                            }
                                            trailing={
                                                isConnectorReady &&
                                                wallet.id ===
                                                    lastUsedWalletId ? (
                                                    <Badge
                                                        size="sm"
                                                        color="success"
                                                    >
                                                        <span className="flex items-center gap-2">
                                                            <IndicationUnit
                                                                color="success"
                                                                size={1}
                                                            />
                                                            Last used
                                                        </span>
                                                    </Badge>
                                                ) : (
                                                    <Text
                                                        variant="fine-print"
                                                        render={<span />}
                                                        className="text-foreground-neutral"
                                                    >
                                                        {isConnectorReady
                                                            ? "Detected"
                                                            : "Unavailable"}
                                                    </Text>
                                                )
                                            }
                                            onClick={(): void => {
                                                if (
                                                    isConnectorReady &&
                                                    !isAnyConnecting
                                                ) {
                                                    void handleWalletSelect(
                                                        wallet
                                                    );
                                                }
                                            }}
                                        >
                                            <Text
                                                variant="body"
                                                render={<span />}
                                                className="flex-1 text-foreground-base"
                                            >
                                                {wallet.name}
                                            </Text>
                                        </Button>
                                    );
                                }

                                const { url, isMobileBrowseLink } =
                                    resolveWalletExternalUrl(wallet, isMobile);
                                return (
                                    <ListItem
                                        key={wallet.id}
                                        itemKey={wallet.id}
                                        href={url}
                                        onClick={(): void =>
                                            captureWalletEvent(
                                                "wallet_connect:provider_link_clicked",
                                                {
                                                    ...walletJourney.context(),
                                                    wallet_provider:
                                                        wallet.name,
                                                    link_type:
                                                        isMobileBrowseLink
                                                            ? "open_wallet_browser"
                                                            : "install"
                                                }
                                            )
                                        }
                                        external={!isMobileBrowseLink}
                                        leading={
                                            wallet.icon ? (
                                                <img
                                                    src={wallet.icon}
                                                    alt={wallet.name}
                                                    className="size-6"
                                                />
                                            ) : undefined
                                        }
                                        trailing={
                                            <span className="flex items-center gap-1 font-sans text-sm text-foreground-neutral">
                                                {isMobileBrowseLink
                                                    ? "Open"
                                                    : "Install"}
                                                <ArrowShortUpRightIcon className="w-auto h-2" />
                                            </span>
                                        }
                                        hoverFillColor="rgb(var(--color-olive-000))"
                                        hoverLineColor="rgb(var(--color-tint-base))"
                                        borderColor="rgb(var(--color-line-base))"
                                    >
                                        <span className="font-sans text-base text-foreground-base">
                                            {wallet.name}
                                        </span>
                                    </ListItem>
                                );
                            })}
                        </div>

                        {showDetectionWarning && (
                            <div className="px-4 py-4">
                                <Alert
                                    variant="default"
                                    outline
                                    className="border-warning-base bg-transparent"
                                >
                                    <AlertTitle>
                                        <Text
                                            variant="small"
                                            render={<h3 />}
                                            className="text-foreground-base"
                                        >
                                            No wallet detected
                                        </Text>
                                    </AlertTitle>
                                    <AlertDescription>
                                        <Text variant="fine-print">
                                            If your wallet is installed, another
                                            browser extension may be
                                            interfering. Try disabling it, then
                                            reload.
                                        </Text>
                                    </AlertDescription>
                                </Alert>
                            </div>
                        )}

                        <div className="px-4 pb-4">
                            <p className="font-sans text-sm text-foreground-neutral text-center">
                                By connecting, you agree to our{" "}
                                <a
                                    onClick={(): void =>
                                        captureWalletEvent(
                                            "wallet_connect:link_clicked",
                                            {
                                                ...walletJourney.context(),
                                                destination: "terms"
                                            }
                                        )
                                    }
                                    href="https://solomonlabs.org/terms-of-use"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline text-foreground-base"
                                >
                                    Terms of Service
                                </a>
                            </p>
                        </div>

                        <div className="h-safe-area-inset-bottom" />
                    </>
                );

                if (isMobileViewport) {
                    return (
                        <Drawer open={open} onOpenChange={handleOpenChange}>
                            <DrawerContent
                                ref={measureCardRef}
                                aria-label="Connect Wallet"
                                className="gap-3 pt-0"
                            >
                                {header}
                                {body}
                            </DrawerContent>
                        </Drawer>
                    );
                }

                return (
                    <Dialog open={open} onOpenChange={handleOpenChange}>
                        <DialogContent
                            ref={measureCardRef}
                            className="w-full max-w-[384px] min-h-[336px] max-h-[80vh] overflow-y-auto flex flex-col gap-3 pt-4 pb-0"
                            aria-label="Connect Wallet"
                        >
                            {header}
                            {body}
                        </DialogContent>
                    </Dialog>
                );
            })()}
        </WalletModalContext.Provider>
    );
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to access wallet modal controls
 *
 * @example
 * ```tsx
 * const { openModal, closeModal, showModal } = useWalletModal();
 *
 * return (
 *   <button onClick={openModal}>Connect Wallet</button>
 * );
 * ```
 */
export function useWalletModal(): WalletModalContextValue {
    const context = useContext(WalletModalContext);
    if (!context) {
        throw new Error(
            "useWalletModal must be used within a WalletModalProvider"
        );
    }
    return context;
}

/**
 * Alias for useWalletModal - provides Jupiter-compatible API
 * @deprecated Use useWalletModal instead
 */
export const useUnifiedWalletContext = useWalletModal;
